import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useHasAcceptedLatestLegal } from '@/hooks/useLegalAcceptance';
import { Checkbox } from '@/components/ui/checkbox';
import { FileText, Loader2 } from 'lucide-react';

/**
 * Blocks the app with a modal until the user accepts the latest terms & privacy.
 * Shown only when the legal versions have changed and the user hasn't accepted them yet.
 */
const LegalAcceptanceGate = ({ children }: { children: React.ReactNode }) => {
  const { accepted, loading, acceptCurrent, versions } = useHasAcceptedLatestLegal();
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (loading || accepted !== false) return <>{children}</>;

  const handleAccept = async () => {
    if (!checked) return;
    setSubmitting(true);
    await acceptCurrent();
    setSubmitting(false);
  };

  return (
    <>
      {children}
      <div className="fixed inset-0 z-[100] bg-background/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto overscroll-contain">
        <div className="bg-card border border-border rounded-2xl max-w-md w-full p-6 shadow-2xl animate-in fade-in zoom-in-95 duration-200 max-h-fit my-auto">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent/10 text-accent mb-4">
            <FileText size={20} />
          </div>
          <h2 className="font-serif text-2xl text-foreground mb-2">Atualizamos nossos termos</h2>
          <p className="text-sm text-subtle leading-relaxed mb-5">
            Para continuar usando o Sola Notes, por favor revise e aceite a versão atual dos nossos Termos de Uso (v{versions.terms}) e Política de Privacidade (v{versions.privacy}).
          </p>
          <label className="flex items-start gap-3 p-3 rounded-lg border border-border bg-background cursor-pointer">
            <Checkbox checked={checked} onCheckedChange={(v) => setChecked(!!v)} className="mt-0.5" />
            <span className="text-xs text-foreground leading-relaxed">
              Li e aceito os{' '}
              <Link to="/termos" target="_blank" className="text-accent hover:underline">Termos de Uso</Link>
              {' '}e a{' '}
              <Link to="/privacidade" target="_blank" className="text-accent hover:underline">Política de Privacidade</Link>.
            </span>
          </label>
          <button
            onClick={handleAccept}
            disabled={!checked || submitting}
            className="w-full mt-4 py-3 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            Aceitar e continuar
          </button>
        </div>
      </div>
    </>
  );
};

export default LegalAcceptanceGate;
